import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  fetchProgressAdminDashboard,
  runProgressSnapshotsNow,
} from '../services/api';
import './ProgressDashboardPage.css';

function fmtHours(h) {
  const n = Number(h);
  if (!Number.isFinite(n)) return '—';
  if (n < 1) return `${Math.round(n * 60)} min`;
  return `${n.toFixed(1)} h`;
}

function fmtDate(value, locale) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
}

function ScoreCell({ current, target }) {
  const cur = Number(current);
  const tgt = Number(target) || 75;
  if (!Number.isFinite(cur)) return <span style={{ color: '#94a3b8' }}>—</span>;
  const pct = Math.min(100, Math.round((cur / tgt) * 100));
  const behind = cur < tgt;
  return (
    <div>
      <strong>
        {cur}% / {tgt}%
      </strong>
      <div className={`progress-score-bar ${behind ? 'behind' : ''}`} style={{ marginTop: 4 }}>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ActivationBadge({ days }) {
  const d = Number(days);
  if (!Number.isFinite(d)) return <span className="progress-badge warn">—</span>;
  if (d <= 0) return <span className="progress-badge expired">J0</span>;
  if (d <= 30) return <span className="progress-badge warn">J{d}</span>;
  return <span className="progress-badge ok">J{d}</span>;
}

export default function ProgressDashboardPage() {
  const { t, i18n } = useTranslation();
  const { isJagE, loading: authLoading } = useAuth();
  const locale = i18n.language;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetchProgressAdminDashboard({ page, limit: 25, search });
      if (resp?.success) {
        setData(resp);
      } else {
        setError(t('progressDashboard.load_error'));
      }
    } catch (err) {
      const code = err?.response?.data?.error;
      setError(
        code === 'jag_e_required'
          ? t('progressDashboard.forbidden')
          : err?.response?.data?.message || err.message || t('progressDashboard.load_error')
      );
    } finally {
      setLoading(false);
    }
  }, [page, search, t]);

  useEffect(() => {
    if (!authLoading && isJagE) load();
  }, [authLoading, isJagE, load]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await runProgressSnapshotsNow();
      setPage(1);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.message || err.message || t('progressDashboard.run_failed'));
    } finally {
      setRunning(false);
    }
  };

  if (authLoading) {
    return (
      <div className="progress-forbidden">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isJagE) {
    return (
      <div className="progress-forbidden">
        <h2>{t('progressDashboard.forbidden')}</h2>
        <p>{t('progressDashboard.forbidden_hint')}</p>
      </div>
    );
  }

  const summary = data?.summary || {};
  const items = data?.items || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1 };

  return (
    <div className="progress-dashboard">
      <div className="progress-dashboard-inner">
        <header className="progress-dashboard-header">
          <h1>{t('progressDashboard.title')}</h1>
          <p>{t('progressDashboard.subtitle')}</p>
          {data?.latestRun && (
            <p className="progress-meta">
              {t('progressDashboard.last_run', {
                at: fmtDate(data.latestRun.computedAt, locale),
                runId: data.latestRun.runId,
              })}
            </p>
          )}
        </header>

        <div className="progress-kpi-grid">
          <div className="progress-card progress-kpi">
            <span className="label">{t('progressDashboard.kpi_tracked')}</span>
            <span className="value">{summary.totalTracked ?? 0}</span>
          </div>
          <div className="progress-card progress-kpi">
            <span className="label">{t('progressDashboard.kpi_on_track')}</span>
            <span className="value">{summary.onTrack ?? 0}</span>
          </div>
          <div className="progress-card progress-kpi">
            <span className="label">{t('progressDashboard.kpi_behind')}</span>
            <span className="value">{summary.behind ?? 0}</span>
          </div>
          <div className="progress-card progress-kpi">
            <span className="label">{t('progressDashboard.kpi_active_access')}</span>
            <span className="value">{summary.activeAccess ?? 0}</span>
          </div>
        </div>

        <div className="progress-card">
          <div className="progress-toolbar">
            <input
              type="search"
              placeholder={t('progressDashboard.search_placeholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
            <button type="button" onClick={() => { setPage(1); setSearch(searchInput.trim()); }}>
              {t('progressDashboard.search')}
            </button>
            <button type="button" onClick={load} disabled={loading}>
              {t('progressDashboard.refresh')}
            </button>
            <button type="button" onClick={handleRunNow} disabled={running}>
              {running ? t('progressDashboard.running') : t('progressDashboard.run_now')}
            </button>
          </div>

          {loading && !data ? (
            <LoadingSpinner />
          ) : error ? (
            <p style={{ color: '#b91c1c' }}>{error}</p>
          ) : (
            <div className="progress-table-wrap">
              <table className="progress-table">
                <thead>
                  <tr>
                    <th>{t('progressDashboard.col_user')}</th>
                    <th>{t('progressDashboard.col_exam')}</th>
                    <th>{t('progressDashboard.col_score')}</th>
                    <th>{t('progressDashboard.col_time_spent')}</th>
                    <th>{t('progressDashboard.col_time_left')}</th>
                    <th>{t('progressDashboard.col_questions')}</th>
                    <th>{t('progressDashboard.col_activation')}</th>
                    <th>{t('progressDashboard.col_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ color: '#94a3b8' }}>
                        {t('progressDashboard.no_data')}
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={`${row.userId}-${row.examId}`}>
                        <td>
                          <div><strong>{row.fullName || row.username}</strong></div>
                          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{row.email}</div>
                        </td>
                        <td>{row.examName || row.examId}</td>
                        <td>
                          <ScoreCell current={row.currentScorePct} target={row.targetScorePct} />
                        </td>
                        <td>{fmtHours(row.timeSpentHours)}</td>
                        <td>{fmtHours(row.timeRemainingHours)}</td>
                        <td>{row.questionsAnswered ?? 0}</td>
                        <td>
                          <ActivationBadge days={row.activationDaysRemaining} />
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
                            {fmtDate(row.activationEndsAt, locale)}
                          </div>
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>{row.preparationStatus || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="progress-pagination">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              ←
            </button>
            <span>
              {t('progressDashboard.page_of', { page: pagination.page, total: pagination.totalPages })}
            </span>
            <button
              type="button"
              disabled={page >= pagination.totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
